import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { FileItem } from '@mnspro-cloud/services';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../Button/Button';
import { Input } from '../../Input';
import { containsIllegalChars, getBaseFilename, getFileextension } from '../Files.utils';
import './FileRenameDialog.scss';

export interface FileRenameDialogProps {
  file: FileItem;
  onCloseClick: () => void;
  onConfirmClick: (fileItem: FileItem, newFilename: string) => void;
}

/**
 * Dialog for renaming an existing file
 */
export const FileRenameDialog: React.FC<FileRenameDialogProps> = ({
  file,
  onCloseClick,
  onConfirmClick,
}) => {
  const { t } = useTranslation();
  const isFolder = file.folder !== undefined;
  const [basename, setBasename] = useState<string>(
    isFolder ? file.name ?? '' : getBaseFilename(file.name ?? ''),
  );

  const handleBasenameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBasename(event.target.value);
  };

  const trimmedBasename = basename.trim();
  const hasIllegalName = trimmedBasename !== '' && containsIllegalChars(trimmedBasename);

  const isConfirmBtnDisabled = () => {
    return trimmedBasename === '' || hasIllegalName;
  };

  const handleConfirmClick = () => {
    const newFilename = isFolder
      ? trimmedBasename
      : `${trimmedBasename}.${getFileextension(file.name ?? '')}`;

    onConfirmClick(file, newFilename);
  };

  return (
    <Dialog open={true}>
      <DialogSurface className="fileRenameDialog" aria-labelledby="fileRenameDialogHeader">
        <DialogBody>
          <DialogTitle id="fileRenameDialogHeader">
            {t('filerename_dialog_header', { defaultValue: 'Datei umbenennen' })}
          </DialogTitle>
          <DialogContent>
            {hasIllegalName && (
              <MessageBar intent="error" className="alert">
                <MessageBarBody>
                  {isFolder
                    ? t('filerename_dialog_illegal_foldername', {
                        defaultValue: 'Der Ordnername enthält ungültige Zeichen',
                      })
                    : t('filerename_dialog_illegal_filename', {
                        defaultValue: 'Der Dateiname enthält ungültige Zeichen',
                      })}
                </MessageBarBody>
              </MessageBar>
            )}
            <Input
              appearance="filled-darker"
              value={basename}
              data-cy="fileRenameFileInput"
              onChange={handleBasenameChange}
              className="newFilenameInput"
              contentAfter={
                !isFolder ? (
                  <span className="fileExtension">.{getFileextension(file.name ?? '')}</span>
                ) : undefined
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={onCloseClick}>{t('close', { defaultValue: 'Schließen' })}</Button>
            <Button
              appearance="primary"
              data-cy="fileRenameSaveButton"
              onClick={handleConfirmClick}
              disabled={isConfirmBtnDisabled()}
            >
              {t('save', { defaultValue: 'Speichern' })}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
